import type { DisplayNodes, FunnelResult } from '@/model/types';
import { formatPercent } from '@/lib/format';
import type { NodeSpec, ViewId, ViewSpec } from './types';

/**
 * Three views over the same model. Each is a pure description; `computeLayout`
 * turns it into geometry and the React tree renders it.
 */

export type MainShareBasis = 'population' | 'previous';

export function mainSpec(
  r: FunnelResult,
  d: DisplayNodes,
  shareBasis: MainShareBasis = 'population',
): ViewSpec {
  const W = 190;
  const exclusionDetail =
    d.excludedPopulation > 0
      ? `${d.subpopulations.length} sources · ${d.excludedPopulation.toLocaleString('en-US')} excluded`
      : `${d.subpopulations.length} source subpopulation${d.subpopulations.length === 1 ? '' : 's'}`;
  return {
    id: 'main',
    title: 'Full-population DSF-E pathway flow',
    desc:
      `Proportional flow from ${d.population.toLocaleString('en-US')} eligible members ` +
      `to ${d.completed.toLocaleString('en-US')} completed follow-ups. ` +
      'A subtle threshold gap marks the difficult care transition from screening into follow-up.',
    palette: 'semantic',
    gap: 70,
    sourceH: 430,
    scaleBase: r.nodes.population,
    shareBase: r.nodes.population,
    shareLabel: 'of population',
    shareMode: shareBasis === 'previous' ? 'parent' : 'base',
    parentShareLabel: shareBasis === 'previous' ? 'of previous' : undefined,
    columns: [
      { label: 'Total population', width: W },
      { label: 'Engaged (reached)', width: W },
      { label: 'Screened', width: W },
      { label: 'Follow-up required', width: W },
      { label: 'Engaged (reached)', width: W },
      { label: 'Follow-up completed', width: 215 },
    ],
    nodes: [
      {
        key: 'population', col: 0, value: r.nodes.population, display: d.population,
        title: 'Total population',
        detail: exclusionDetail,
        tone: 'source', stage: 'source',
      },
      {
        key: 'reachedScreening', col: 1, value: r.nodes.reachedScreening, display: d.reachedScreening,
        title: 'Engaged (reached)', detail: 'Live contact, screening', tone: 'outreach', stage: 'reach',
      },
      {
        key: 'neverReached', col: 1, value: r.nodes.neverReached, display: d.neverReached,
        title: 'Never reached', tone: 'closed', stage: 'reach',
      },
      {
        key: 'screened', col: 2, value: r.nodes.screened, display: d.screened,
        title: 'Screened', detail: 'Completed ≥PHQ-2', tone: 'screen', stage: 'screen', explode: 'screened',
      },
      {
        key: 'notScreened', col: 2, value: r.nodes.notScreened, display: d.notScreened,
        title: 'Reached, not screened', tone: 'recoverable', stage: 'screen', explode: 'screened',
      },
      {
        key: 'followupRequired', col: 3, value: r.nodes.followupRequired, display: d.followupRequired,
        title: 'Follow-up required', detail: 'PHQ-9 10 or higher',
        tone: 'chase', stage: 'chase', explode: 'followup',
      },
      {
        key: 'cssrsNeeded', col: 3, value: r.nodes.cssrsNeeded, display: d.cssrsNeeded,
        title: 'C-SSRS required', detail: 'Overlay · still in the chase pool',
        tone: 'clinical', stage: 'screen', explode: 'screened', minH: 92,
      },
      {
        key: 'phq2Negative', col: 3, value: r.nodes.phq2Negative, display: d.phq2Negative,
        title: 'PHQ-2 negative', detail: 'Under 3; no PHQ-9 needed',
        tone: 'compliant', stage: 'screen', explode: 'screened',
      },
      {
        key: 'phq9UnderTen', col: 3, value: r.nodes.phq9UnderTen, display: d.phq9UnderTen,
        title: 'PHQ-9 under 10', detail: 'Screened positive; measure closed',
        tone: 'compliant', stage: 'screen', explode: 'screened',
      },
      {
        key: 'reachedFollowup', col: 4, value: r.nodes.reachedFollowup, display: d.reachedFollowup,
        title: 'Engaged (reached)', detail: 'Live contact, follow-up', tone: 'outreach', stage: 'chase',
      },
      {
        key: 'notReachedFollowup', col: 4, value: r.nodes.notReachedFollowup, display: d.notReachedFollowup,
        title: 'Never reached again', tone: 'closed', stage: 'chase',
      },
      {
        key: 'completed', col: 5, value: r.nodes.completed, display: d.completed,
        title: 'Follow-up completed', detail: 'Qualifying encounter done', tone: 'qualifying', stage: 'chase',
      },
      {
        key: 'notCompleted', col: 5, value: r.nodes.notCompleted, display: d.notCompleted,
        title: 'Reached, not completed', tone: 'recoverable', stage: 'chase',
      },
    ],
    flows: [
      { from: 'population', children: ['reachedScreening', 'neverReached'], denominatorLabel: 'the population' },
      { from: 'reachedScreening', children: ['screened', 'notScreened'], denominatorLabel: 'those reached' },
      { from: 'screened', children: ['followupRequired', 'phq2Negative', 'phq9UnderTen'], denominatorLabel: 'those screened' },
      { from: 'followupRequired', children: ['cssrsNeeded'], denominatorLabel: 'the chase pool', kind: 'overlay' },
      { from: 'followupRequired', children: ['reachedFollowup', 'notReachedFollowup'], denominatorLabel: 'the chase pool' },
      { from: 'reachedFollowup', children: ['completed', 'notCompleted'], denominatorLabel: 'those reached' },
    ],
    gates: [
      { after: 'population', rate: r.rates.screeningReach },
      { after: 'reachedScreening', rate: r.rates.screenGivenReached },
      { after: 'screened', rate: r.rates.followupRequiredShare },
      { after: 'followupRequired', rate: r.rates.followupReach },
      { after: 'reachedFollowup', rate: r.rates.followupCompletion },
    ],
    difficultyBreak: {
      after: 'screened',
      label: 'Difficult care transition from screening into follow-up',
    },
  };
}

export function screenedSpec(r: FunnelResult, d: DisplayNodes): ViewSpec {
  const W = 230;
  return {
    id: 'screened',
    title: 'Screening step, exploded',
    desc: 'The screening step broken into its three mutually exclusive outcomes.',
    gap: 90,
    sourceH: 400,
    scaleBase: r.nodes.reachedScreening,
    shareBase: r.nodes.population,
    shareLabel: 'of population',
    shareMode: 'parent',
    numbered: false,
    selectable: false,
    palette: 'mono',
    columns: [
      { label: 'Engaged (reached)', width: W },
      { label: 'Screened', width: W },
      { label: 'PHQ-9 threshold', width: W },
      { label: 'Instrument detail', width: W },
    ],
    nodes: [
      {
        key: 'reachedScreening', col: 0, value: r.nodes.reachedScreening, display: d.reachedScreening,
        title: 'Engaged (reached)', detail: 'Live contact, screening', tone: 'outreach', stage: 'reach',
      },
      {
        key: 'screened', col: 1, value: r.nodes.screened, display: d.screened,
        title: 'Screened', detail: 'Completed ≥PHQ-2', tone: 'screen', stage: 'screen',
      },
      {
        key: 'notScreened', col: 1, value: r.nodes.notScreened, display: d.notScreened,
        title: 'Reached, not screened', tone: 'recoverable', stage: 'screen',
      },
      {
        key: 'followupRequired', col: 2, value: r.nodes.followupRequired, display: d.followupRequired,
        title: 'Follow-up required', detail: 'PHQ-9 10 or higher', tone: 'chase', stage: 'screen',
      },
      {
        key: 'noFollowupNeeded', col: 2, value: r.nodes.noFollowupNeeded, display: d.noFollowupNeeded,
        title: 'No follow-up needed', detail: 'PHQ-9 under 10; measure closed',
        tone: 'compliant', stage: 'screen',
      },
      {
        key: 'cssrsNeeded', col: 3, value: r.nodes.cssrsNeeded, display: d.cssrsNeeded,
        title: 'C-SSRS required', detail: 'Overlay · still owes its follow-up',
        tone: 'clinical', stage: 'screen', minH: 92,
      },
      {
        key: 'phq2Negative', col: 3, value: r.nodes.phq2Negative, display: d.phq2Negative,
        title: 'PHQ-2 negative', detail: 'Under 3; no PHQ-9 administered',
        tone: 'compliant', stage: 'screen',
      },
      {
        key: 'phq9UnderTen', col: 3, value: r.nodes.phq9UnderTen, display: d.phq9UnderTen,
        title: 'PHQ-9 under 10', detail: 'PHQ-2 positive, PHQ-9 completed',
        tone: 'compliant', stage: 'screen',
      },
    ],
    flows: [
      { from: 'reachedScreening', children: ['screened', 'notScreened'], denominatorLabel: 'those reached' },
      { from: 'screened', children: ['followupRequired', 'noFollowupNeeded'], denominatorLabel: 'those screened' },
      { from: 'noFollowupNeeded', children: ['phq2Negative', 'phq9UnderTen'], denominatorLabel: 'the no-follow-up group' },
      { from: 'followupRequired', children: ['cssrsNeeded'], denominatorLabel: 'the follow-up group', kind: 'overlay' },
    ],
    gates: [{ after: 'reachedScreening', rate: r.rates.screenGivenReached }],
  };
}

export function followupSpec(r: FunnelResult, d: DisplayNodes): ViewSpec {
  const W = 205;
  const nodes: NodeSpec[] = [
    {
      key: 'followupRequired', col: 0, lane: 0, spanLanes: true,
      value: r.nodes.followupRequired, display: d.followupRequired,
      title: 'Screened PHQ-9 positive', detail: 'The whole chase pool',
      tone: 'chase', stage: 'chase',
    },
  ];
  const poolChildren: string[] = [];
  const flows: ViewSpec['flows'] = [];

  r.cohorts.forEach((c, i) => {
    const dc = d.cohorts[i];
    if (!dc) return;
    poolChildren.push(`pool:${c.id}`);
    nodes.push(
      {
        key: `pool:${c.id}`, col: 1, lane: i, value: c.pool, display: dc.pool,
        title: c.name, detail: `${formatPercent(c.share)} of the pool`, tone: 'cohort', stage: 'chase',
      },
      {
        key: `reached:${c.id}`, col: 2, lane: i, value: c.reached, display: dc.reached,
        title: 'Engaged (reached)', detail: `${formatPercent(c.reach)} reach`, tone: 'outreach', stage: 'chase',
      },
      {
        key: `notReached:${c.id}`, col: 2, lane: i, value: c.notReached, display: dc.notReached,
        title: 'Never reached', tone: 'closed', stage: 'chase',
      },
      {
        key: `completed:${c.id}`, col: 3, lane: i, value: c.completed, display: dc.completed,
        title: 'Follow-up completed', detail: `${formatPercent(c.completion)} completion`,
        tone: 'qualifying', stage: 'chase',
      },
      {
        key: `notCompleted:${c.id}`, col: 3, lane: i, value: c.notCompleted, display: dc.notCompleted,
        title: 'Not completed', tone: 'recoverable', stage: 'chase',
      },
    );
    flows.push(
      {
        from: `pool:${c.id}`,
        children: [`reached:${c.id}`, `notReached:${c.id}`],
        denominatorLabel: `the ${c.name} pool`,
      },
      {
        from: `reached:${c.id}`,
        children: [`completed:${c.id}`, `notCompleted:${c.id}`],
        denominatorLabel: 'those reached',
      },
    );
  });

  return {
    id: 'followup',
    title: 'Follow-up step, exploded',
    desc: 'The chase pool split into cohorts, each with its own reach and completion.',
    gap: 80,
    sourceH: 420,
    scaleBase: r.nodes.followupRequired,
    shareBase: r.nodes.population,
    shareLabel: 'of population',
    shareMode: 'parent',
    numbered: false,
    selectable: false,
    palette: 'chase',
    columns: [
      { label: 'Screened PHQ-9 positive', width: W },
      { label: 'Subpopulation', width: W },
      { label: 'Engaged (reached)', width: W },
      { label: 'Follow-up completed', width: W },
    ],
    nodes,
    flows: [
      {
        from: 'followupRequired',
        children: poolChildren,
        denominatorLabel: 'the chase pool',
        partitionChildren: true,
      },
      ...flows,
    ],
  };
}

export function specForView(view: ViewId, r: FunnelResult, d: DisplayNodes): ViewSpec {
  if (view === 'screened') return screenedSpec(r, d);
  if (view === 'followup') return followupSpec(r, d);
  return mainSpec(r, d);
}
